import React from 'react';
import { Badge } from '../ui/badge';
import { DollarSign, Home, Briefcase, Globe, TrendingUp, ThumbsUp, ThumbsDown } from 'lucide-react';

const fmt = (n) => (typeof n === 'number' ? `$${n.toLocaleString()}` : n || '—');

const levelColor = (v) => {
  const s = String(v || '').toLowerCase();
  if (s.startsWith('high') || s.startsWith('large') || s.startsWith('strong')) return 'bg-green-500/30 text-green-200';
  if (s.startsWith('med')) return 'bg-yellow-500/30 text-yellow-200';
  return 'bg-red-500/30 text-red-200';
};

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="p-3 rounded-lg bg-white/5">
    <p className="text-xs text-white/50 flex items-center gap-1"><Icon className="h-3 w-3" />{label}</p>
    <p className={`font-semibold ${accent || 'text-white'}`}>{value}</p>
  </div>
);

export const CityCard = ({ city, tag }) => (
  <div data-testid={`city-result-${city.city?.split(',')[0].toLowerCase().replace(/\s+/g, '-')}`} className="rounded-xl border border-white/15 bg-white/5 p-4 space-y-3">
    <div className="flex items-start justify-between gap-2">
      <div>
        <h4 className="text-lg font-bold text-white">{city.city}</h4>
        <p className="text-xs text-white/60">Cost of living index {city.cost_of_living_index} (US avg 100) · vs national {city.salary_vs_national}</p>
      </div>
      {tag && <Badge className="bg-amber-400/90 text-slate-900 whitespace-nowrap">{tag}</Badge>}
    </div>

    <div className="grid grid-cols-3 gap-2">
      <Stat icon={DollarSign} label="Entry" value={fmt(city.entry_salary)} accent="text-green-400" />
      <Stat icon={DollarSign} label="Mid" value={fmt(city.mid_salary)} accent="text-green-400" />
      <Stat icon={DollarSign} label="Senior" value={fmt(city.senior_salary)} accent="text-green-400" />
    </div>
    <div className="grid grid-cols-3 gap-2">
      <Stat icon={Home} label="1BR Rent / mo" value={fmt(city.median_rent_1br)} />
      <Stat icon={TrendingUp} label="COL-adjusted entry" value={fmt(city.adjusted_entry_salary)} accent="text-cyan-300" />
      <Stat icon={Briefcase} label="Open roles" value={city.job_openings_estimate} />
    </div>

    <div className="flex flex-wrap gap-2 text-xs">
      <Badge className={levelColor(city.demand_level)}>Demand: {city.demand_level}</Badge>
      <Badge className={levelColor(city.h1b_sponsorship)}><Globe className="h-3 w-3 mr-1" />H-1B: {city.h1b_sponsorship}</Badge>
      <Badge className={levelColor(city.international_community)}>Intl community: {city.international_community}</Badge>
      <Badge className={levelColor(city.growth_outlook)}>Growth: {city.growth_outlook}</Badge>
    </div>

    {city.top_employers?.length > 0 && (
      <div>
        <p className="text-xs text-white/50 mb-1">Top employers</p>
        <div className="flex flex-wrap gap-1">
          {city.top_employers.map((e, i) => <Badge key={i} className="bg-white/10 text-white/80 text-xs">{e}</Badge>)}
        </div>
      </div>
    )}

    <div className="grid grid-cols-2 gap-3 text-xs">
      <ul className="space-y-1">
        {city.pros?.map((p, i) => <li key={i} className="flex gap-1 text-green-200"><ThumbsUp className="h-3 w-3 mt-0.5 flex-shrink-0" />{p}</li>)}
      </ul>
      <ul className="space-y-1">
        {city.cons?.map((c, i) => <li key={i} className="flex gap-1 text-red-200"><ThumbsDown className="h-3 w-3 mt-0.5 flex-shrink-0" />{c}</li>)}
      </ul>
    </div>

    {city.verdict && <p className="text-sm text-white/80 italic border-t border-white/10 pt-2">{city.verdict}</p>}
  </div>
);
